import React, { useState } from 'react';
import { generateCharacterSVG } from '../utils/characterSVG';
import { characterData } from '../data/characterData';
import { useLanguage } from '../contexts/LanguageContext';

const CharacterCollection = ({ unlockedCharacters, currentCharacter, onCharacterSelect }) => {
  const { t } = useLanguage();

  const characters = [
    { id: 'unicorn', unlockText: t('unlockConditions.default') },
    { id: 'dragon', unlockText: t('unlockConditions.practice10min') },
    { id: 'panda', unlockText: t('unlockConditions.practice20min') },
    { id: 'fox', unlockText: t('unlockConditions.try5speeds') },
    { id: 'alien', unlockText: t('unlockConditions.tryAllAccessories') },
    { id: 'robot', unlockText: t('unlockConditions.practice1hour') },
    { id: 'wizard', unlockText: t('unlockConditions.practice2hours') },
    { id: 'ninja', unlockText: t('unlockConditions.tryAllTimeSigs') },
    { id: 'princess', unlockText: t('unlockConditions.unlockAllOthers') },
    { id: 'superhero', unlockText: t('unlockConditions.completeAllAchievements') }
  ];

  // 只有最後兩個角色顯示為神秘角色
  const isMysteryCharacter = (characterId) => {
    return characterId === 'princess' || characterId === 'superhero';
  };

  const [selectedCharacter, setSelectedCharacter] = useState(null);

  const handleCardClick = (characterId) => {
    // 只是選擇查看，不跳轉頁面
    setSelectedCharacter(characterId);
  };

  return (
    <div className="space-y-6">
      {/* 收集進度 */}
      <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl">
        <div className="text-center">
          <h3 className="text-2xl font-bold mb-2 text-gray-800 dark:text-gray-100">
            {t('characterCollection')}
          </h3>
          <div className="flex items-center justify-center space-x-4 mb-4">
            <div className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              {unlockedCharacters.length}
            </div>
            <div className="text-2xl text-gray-400">/</div>
            <div className="text-2xl font-bold text-gray-600 dark:text-gray-300">10</div>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-4">
            <div
              className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${(unlockedCharacters.length / 10) * 100}%` }}
            />
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {unlockedCharacters.length === 10 
              ? t('allCharactersUnlocked') || '🎉 所有角色都已解鎖！' 
              : t('keepPracticingToUnlock') || '繼續練習以解鎖更多角色'}
          </p>
        </div>
      </div>

      {/* 角色網格 */}
      <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {characters.map(character => {
            const isUnlocked = unlockedCharacters.includes(character.id);
            const isSelected = selectedCharacter === character.id;
            const charData = characterData[character.id];
            const isMystery = isMysteryCharacter(character.id) && !isUnlocked;

            return (
              <div
                key={character.id}
                onClick={() => handleCardClick(character.id)}
                className={`relative p-4 rounded-2xl transition-all duration-200 cursor-pointer group hover:shadow-xl ${
                  isSelected 
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg transform scale-105' 
                    : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                } ${!isUnlocked ? 'opacity-75' : ''}`}
              >
                {/* 角色圖示 */}
                <div className="w-16 h-16 mx-auto mb-3 relative">
                  {isMystery ? (
                    <div className="w-full h-full bg-gray-400 dark:bg-gray-600 rounded-full flex items-center justify-center">
                      <span className="text-2xl">❓</span>
                    </div>
                  ) : (
                    <div
                      className={`w-full h-full transition-transform duration-200 ${
                        isSelected ? 'animate-bounce' : 'group-hover:scale-110'
                      }`}
                      dangerouslySetInnerHTML={{
                        __html: generateCharacterSVG(
                          character.id,
                          charData?.defaultColor || '#5D5CDE'
                        )
                      }}
                    />
                  )}
                </div>

                {/* 角色名稱 */}
                <h4
                  className={`text-center font-bold text-sm mb-2 ${
                    isSelected ? 'text-white' : 'text-gray-800 dark:text-gray-100'
                  }`}
                >
                  {isMystery ? t('mysteryCharacter') : t(`characters.${character.id}.name`)}
                </h4>

                {/* 解鎖條件 */}
                <p
                  className={`text-xs text-center leading-tight w-full mx-auto ${
                    isSelected ? 'text-white opacity-90' : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {character.unlockText}
                </p>

                {/* 鎖定圖示 */}
                {!isUnlocked && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-gray-500 dark:bg-gray-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">🔒</span>
                  </div>
                )}

                {/* 選中標記 */}
                {isSelected && (
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-sm font-bold">👁️</span>
                  </div>
                )}

                {/* 已解鎖標記 */}
                {isUnlocked && !isSelected && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">✓</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 選中角色詳情 */}
      {selectedCharacter && !isMysteryCharacter(selectedCharacter) && (
        <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl">
          <div className="text-center">
            <h4 className="text-xl font-bold mb-4 text-gray-800 dark:text-gray-100">
              {t(`characters.${selectedCharacter}.name`)}
            </h4>

            <div className="flex items-center justify-center mb-6">
              <div
                className="w-24 h-24 animate-bounce"
                dangerouslySetInnerHTML={{
                  __html: generateCharacterSVG(
                    selectedCharacter,
                    characterData[selectedCharacter]?.defaultColor || '#5D5CDE'
                  )
                }}
              />
            </div>

            <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed max-w-md mx-auto">
              {t(`characters.${selectedCharacter}.description`)}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md mx-auto">
              <div className="bg-purple-100 dark:bg-purple-900 rounded-xl p-4 flex flex-col items-center">
                <div className="text-lg mb-2">✨</div>
                <p className="text-sm font-medium text-purple-800 dark:text-purple-200 mb-1 text-center">
                  {t('specialAbility')}
                </p>
                <p className="text-sm text-purple-600 dark:text-purple-300 text-center">
                  {t(`characters.${selectedCharacter}.ability`)}
                </p>
              </div>

              <div className="bg-blue-100 dark:bg-blue-900 rounded-xl p-4 flex flex-col items-center">
                <div className="text-lg mb-2">🎵</div>
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1 text-center">
                  {t('favoriteType')}
                </p>
                <p className="text-sm text-blue-600 dark:text-blue-300 text-center">
                  {t(`characters.${selectedCharacter}.favoriteMusic`)}
                </p>
              </div>
            </div>

            {unlockedCharacters.includes(selectedCharacter) && (
              <button
                onClick={() => onCharacterSelect(selectedCharacter)}
                className="mt-6 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-bold hover:scale-105 transition-transform duration-200"
              >
                {t('switchToCharacterPage') || '切換到角色頁面'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CharacterCollection;